import { User, PaginatedResponse } from "@/types";
import { apiClient } from "@/lib/apiClient";

/**
 * User Service
 * Handles user-related API calls
 */
export const userService = {
  /**
   * Get all users in the organization with optional search
   * Useful for assigning members to teams
   */
  getCompanyUsers: async (search?: string, page = 1, limit = 10): Promise<PaginatedResponse<User>> => {
    return apiClient.get<PaginatedResponse<User>>("/api/users", {
      params: {
        ...(search ? { fullName: search } : {}),
        page: page.toString(),
        limit: limit.toString(),
      },
    });
  },

  /**
   * Create a new user
   */
  createUser: async (data: Partial<User>): Promise<User> => {
    return apiClient.post<User>("/api/users", data);
  },

  /**
   * Update a user's general information
   */
  updateUser: async (userPid: string, data: Partial<User>): Promise<User> => {
    return apiClient.put<User>(`/api/users/${userPid}`, data);
  },

  /**
   * Update a user's role (specialized update)
   */
  updateUserRole: async (userPid: string, roleName: string): Promise<User> => {
    return apiClient.put<User>(`/api/users/${userPid}`, { roleName });
  },

  /**
   * Delete a user
   */
  deleteUser: async (userPid: string): Promise<void> => {
    return apiClient.delete<void>(`/api/users/${userPid}`);
  },

  /**
   * Assign a manager to a user (add direct report)
   */
  assignManager: async (userPid: string, managerPid: string): Promise<User> => {
    return apiClient.put<User>(`/api/users/${userPid}`, { managerPid });
  },

  /**
   * Remove manager from a user (remove direct report)
   */
  removeManager: async (userPid: string): Promise<User> => {
    return apiClient.put<User>(`/api/users/${userPid}`, { managerPid: null });
  },
};
